/**
 * Issue ownership — conservative remediation routing for governance findings.
 *
 * This classifies the most likely owner of a finding; it never changes
 * detection, severity, gating, or seed data. Ambiguous didactic and
 * family-wide signals deliberately route to review before any edit is made.
 */

import type { IssueCode, IssueDomain } from './issues/codes';
import type { ValidationIssue } from './issues/types';
export type IssueOwner = 'seed' | 'rulebook' | 'platform-registry' | 'review-first';
export interface IssueOwnership {
  owner: IssueOwner;
  label: string;
  route: string;
}
const REVIEW_FIRST_CODES = new Set<IssueCode>(['SCHEMA_DIFFICULTY_TIER_INVERSION', 'CONTENT_BASIS_COGNITIVE_TOO_HIGH', 'CONTENT_EIND_COGNITIVE_TOO_LOW', 'CONTENT_PLUS_INADEQUATE', 'PEDAGOGY_DOK_TOO_LOW', 'PEDAGOGY_DOK_TOO_HIGH', 'DIVERSITY_TOO_FEW_TYPES', 'DIVERSITY_NO_INTERACTIVE', 'DIVERSITY_LOW_VARIETY', 'DIVERSITY_NO_DOK3_TYPE', 'DIVERSITY_TYPE_OVERUSE', 'DIVERSITY_PRODUCTION_RECOGNITION_SKEW', 'ASSESSMENT_POOL_CAPACITY', 'ASSESSMENT_POOL_DISTRIBUTION', 'ASSESSMENT_POOL_FEASIBILITY']);
const RULEBOOK_CODES = new Set<IssueCode>(['VISUAL_RULEBOOK_HINT_STIMULUS_MISMATCH', 'VISUAL_RULEBOOK_HINT_SUPPORT_MISMATCH', 'VISUAL_RULEBOOK_HINT_FAMILY_DRIFT', 'PEDAGOGY_FAMILY_TYPE_DISCOURAGED']);
const PLATFORM_REGISTRY_CODES = new Set<IssueCode>(['SCHEMA_UNKNOWN_FAMILY', 'TYPE_UNKNOWN', 'TYPE_MODE_NOT_SUPPORTED', 'VISUAL_UNKNOWN_AID_TYPE', 'VISUAL_DATA_SCHEMA_VIOLATION', 'SUPPORT_FAMILY_NOT_IN_REGISTRY', 'PEDAGOGY_MISCONCEPTION_UNKNOWN', 'SUBVALIDATOR_CRASHED']);
const OWNERSHIP: Record<IssueOwner, IssueOwnership> = {
  seed: {
    owner: 'seed',
    label: 'Seed',
    route: 'Werk de vraag of seedinhoud bij.'
  },
  rulebook: {
    owner: 'rulebook',
    label: 'Rulebook',
    route: 'Controleer eerst de didactische regel of family-hint.'
  },
  'platform-registry': {
    owner: 'platform-registry',
    label: 'Platform/registry',
    route: 'Herstel de registry, het schema, de renderer of het typecontract.'
  },
  'review-first': {
    owner: 'review-first',
    label: 'Eerst beoordelen',
    route: 'Beoordeel seed, rulebook en platform samen vóór je iets wijzigt.'
  }
};

/**
 * Resolve the likely remediation owner for one finding.
 *
 * Direct authored-content omissions default to Seed. Policy interpretation,
 * difficulty, cognitive, diversity and capacity signals default to
 * Eerst beoordelen, because no individual seed edit is safe to prescribe.
 */
export function resolveIssueOwnership(issue: Pick<ValidationIssue, 'code' | 'domain'>): IssueOwnership {
  if (REVIEW_FIRST_CODES.has(issue.code)) return OWNERSHIP['review-first'];
  if (RULEBOOK_CODES.has(issue.code)) return OWNERSHIP.rulebook;
  if (PLATFORM_REGISTRY_CODES.has(issue.code) || issue.code.startsWith('GRAPH_')) {
    return OWNERSHIP['platform-registry'];
  }
  if (issue.domain === 'assessmentPool' || issue.domain === 'diversity' || issue.domain === 'familyStats') {
    return OWNERSHIP['review-first'];
  }
  return OWNERSHIP.seed;
}
export function getIssueOwnershipLabel(owner: IssueOwner): string {
  return OWNERSHIP[owner].label;
}